import React, { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import {
  Circle,
  CircleMarker,
  MapContainer as LeafletMap,
  TileLayer,
  useMap,
} from "react-leaflet";
import { useTranslation } from "react-i18next";
import { getGroupsByRadius } from "../../../api";
import {
  buildTypeOfFishingFilterOptions,
  getTypeOfFishingOptionsForWay,
  WAY_OF_FISHING_OPTIONS,
} from "../../../utils/catchMetadataOptions";

const GROUP_WATER_TYPE_OPTIONS = [
  { value: "all", defaultLabel: "All" },
  { value: "lake", defaultLabel: "Lake" },
  { value: "river", defaultLabel: "River" },
  { value: "freshwater_any", defaultLabel: "Freshwater any" },
  { value: "saltwater_marine", defaultLabel: "Salt water (marine)" },
];

function FitRadiusBounds({ lat, lng, radiusMeters }) {
  const map = useMap();

  useEffect(() => {
    if (!map) return;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    if (!Number.isFinite(radiusMeters) || radiusMeters <= 0) return;

    let cancelled = false;

    const fitBounds = () => {
      if (cancelled) return;

      const center = L.latLng(lat, lng);
      const bounds = center.toBounds(radiusMeters);

      map.fitBounds(bounds, {
        padding: [24, 24],
        maxZoom: radiusMeters <= 10000 ? 12 : 10,
      });
    };

    if (map._loaded) {
      fitBounds();
    } else {
      map.whenReady(fitBounds);
    }

    return () => {
      cancelled = true;
    };
  }, [lat, lng, map, radiusMeters]);

  return null;
}

export default function GroupsRadiusPanel({ data, onOpenGroup }) {
  const { t } = useTranslation();
  const requestIdRef = useRef(0);
  const [radiusKm, setRadiusKm] = useState(25);
  const [appliedRadiusKm, setAppliedRadiusKm] = useState(25);
  const [groupFilterWaterType, setGroupFilterWaterType] = useState("all");
  const [groupFilterWayOfFishing, setGroupFilterWayOfFishing] = useState("all");
  const [groupFilterTypeOfFishing, setGroupFilterTypeOfFishing] = useState("all");
  const [fishingMethods, setFishingMethods] = useState({});
  const [results, setResults] = useState([]);
  const [resultCount, setResultCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [hasAppliedSearch, setHasAppliedSearch] = useState(false);

  const lat = Number(data?.lat);
  const lng = Number(data?.lng);
  const radiusMeters = radiusKm * 1000;
  const placeName = data?.placeName || t("weather_selected_location");
  const formatDistanceLabel = (distance) =>
    t("weather_groups_results_distance", { distance });
  const getVisibilityLabel = (visibility) =>
    visibility === "private" ? t("groups_private") : t("groups_public");
  const getGroupTypeLabel = (groupType) =>
    groupType === "local" ? t("groups_type_local") : t("groups_type_general");
  const groupTypeOfFishingOptions = useMemo(() => {
    if (groupFilterWayOfFishing === "all") {
      return buildTypeOfFishingFilterOptions(fishingMethods).map((option) => option.label);
    }

    return getTypeOfFishingOptionsForWay(fishingMethods, groupFilterWayOfFishing);
  }, [fishingMethods, groupFilterWayOfFishing]);

  useEffect(() => {
    let isMounted = true;

    fetch(`${process.env.PUBLIC_URL}/JSON/fishingMethods.json`)
      .then((response) => response.json())
      .then((nextFishingMethods) => {
        if (isMounted && nextFishingMethods && typeof nextFishingMethods === "object") {
          setFishingMethods(nextFishingMethods);
        }
      })
      .catch(() => {
        if (isMounted) {
          setFishingMethods({});
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    requestIdRef.current += 1;
    setRadiusKm(25);
    setAppliedRadiusKm(25);
    setGroupFilterWaterType("all");
    setGroupFilterWayOfFishing("all");
    setGroupFilterTypeOfFishing("all");
    setResults([]);
    setResultCount(0);
    setIsLoading(false);
    setErrorMessage("");
    setHasAppliedSearch(false);
  }, [lat, lng]);

  const handleGroupFilterWayOfFishingChange = (nextWayOfFishing) => {
    setGroupFilterWayOfFishing(nextWayOfFishing);
    setGroupFilterTypeOfFishing("all");
  };

  const handleApply = async () => {
    const token =
      typeof window !== "undefined" ? window.localStorage.getItem("token") : "";
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    setAppliedRadiusKm(radiusKm);
    setHasAppliedSearch(true);
    setIsLoading(true);
    setErrorMessage("");

    try {
      const data = await getGroupsByRadius(lat, lng, radiusKm, token, {
        waterType: groupFilterWaterType,
        wayOfFishing: groupFilterWayOfFishing,
        typeOfFishing: groupFilterTypeOfFishing,
      });
      if (requestIdRef.current !== requestId) {
        return;
      }

      const nextResults = Array.isArray(data?.results) ? data.results : [];
      setResults(nextResults);
      setResultCount(
        typeof data?.count === "number" ? data.count : nextResults.length
      );
    } catch (error) {
      if (requestIdRef.current !== requestId) {
        return;
      }

      console.error("Failed to load groups by radius:", error);
      setResults([]);
      setResultCount(0);
      setErrorMessage(t("weather_groups_results_error"));
    } finally {
      if (requestIdRef.current !== requestId) {
        return;
      }
      setIsLoading(false);
    }
  };

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }

  return (
    <div className="anglers-radius-panel">
      <div className="anglers-radius-header">
        <h3>{t("groups")}</h3>
        <p>{t("weather_groups_intro")}</p>
      </div>

      <div className="anglers-radius-map-meta">
        <strong>{placeName}</strong>
        <span>
          {t("common_lat")}: {lat.toFixed(3)}, {t("common_lng")}:{" "}
          {lng.toFixed(3)}
        </span>
      </div>

      <div className="anglers-radius-map-shell">
        <LeafletMap
          center={[lat, lng]}
          zoom={10}
          className="anglers-radius-map"
          attributionControl={false}
          zoomControl={false}
          dragging={false}
          doubleClickZoom={false}
          scrollWheelZoom={false}
          touchZoom={false}
          boxZoom={false}
          keyboard={false}
        >
          <TileLayer
            attribution="&copy; OpenStreetMap contributors"
            url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
          />
          <FitRadiusBounds lat={lat} lng={lng} radiusMeters={radiusMeters} />
          <Circle
            center={[lat, lng]}
            radius={radiusMeters}
            pathOptions={{
              color: "#1976d2",
              fillColor: "#4aa3ff",
              fillOpacity: 0.16,
              weight: 2,
            }}
          />
          <CircleMarker
            center={[lat, lng]}
            radius={6}
            pathOptions={{
              color: "#0f5d8c",
              fillColor: "#1fa4c5",
              fillOpacity: 1,
              weight: 2,
            }}
          />
        </LeafletMap>
      </div>

      <div className="anglers-radius-controls">
        <div className="anglers-radius-summary">
          <span>{t("weather_groups_radius_label")}</span>
          <strong>{formatDistanceLabel(radiusKm)}</strong>
        </div>

        <input
          type="range"
          min="5"
          max="500"
          step="5"
          value={radiusKm}
          onChange={(event) => setRadiusKm(Number(event.target.value))}
          className="anglers-radius-slider"
        />

        <div className="anglers-radius-scale">
          <span>{formatDistanceLabel(5)}</span>
          <span>{formatDistanceLabel(500)}</span>
        </div>

        <div className="groups-radius-filters">
          <div className="groups-radius-filter-field">
            <label htmlFor="groups-radius-water-type">
              {t("groups_water_type", { defaultValue: "Water type" })}
            </label>
            <select
              id="groups-radius-water-type"
              value={groupFilterWaterType}
              onChange={(event) => setGroupFilterWaterType(event.target.value)}
            >
              {GROUP_WATER_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.value === "all"
                    ? t("all", { defaultValue: option.defaultLabel })
                    : t(`groups_water_type_${option.value}`, {
                        defaultValue: option.defaultLabel,
                      })}
                </option>
              ))}
            </select>
          </div>

          <div className="groups-radius-filter-field">
            <label htmlFor="groups-radius-way-of-fishing">
              {t("catch_way_of_fishing", { defaultValue: "Way of Fishing" })}
            </label>
            <select
              id="groups-radius-way-of-fishing"
              value={groupFilterWayOfFishing}
              onChange={(event) => handleGroupFilterWayOfFishingChange(event.target.value)}
            >
              <option value="all">{t("all", { defaultValue: "All" })}</option>
              {WAY_OF_FISHING_OPTIONS.map((way) => (
                <option key={way} value={way}>
                  {way === "Shore"
                    ? t("fishing_way_shore", { defaultValue: way })
                    : way === "Boat"
                      ? t("fishing_way_boat", { defaultValue: way })
                      : way}
                </option>
              ))}
            </select>
          </div>

          <div className="groups-radius-filter-field">
            <label htmlFor="groups-radius-type-of-fishing">
              {t("catch_type_of_fishing", { defaultValue: "Type of Fishing" })}
            </label>
            <select
              id="groups-radius-type-of-fishing"
              value={groupFilterTypeOfFishing}
              onChange={(event) => setGroupFilterTypeOfFishing(event.target.value)}
            >
              <option value="all">{t("all", { defaultValue: "All" })}</option>
              {groupTypeOfFishingOptions.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
        </div>

        <button
          type="button"
          className="anglers-radius-apply-btn"
          onClick={handleApply}
          disabled={isLoading}
        >
          {isLoading
            ? t("weather_groups_loading")
            : t("apply")}
        </button>
      </div>

      <div className="anglers-radius-results">
        <h4>{t("weather_groups_results_title")}</h4>
        {!hasAppliedSearch && (
          <p>{t("weather_groups_results_placeholder")}</p>
        )}
        {isLoading && (
          <p>{t("weather_groups_results_loading")}</p>
        )}
        {!isLoading && errorMessage && (
          <p className="anglers-radius-results-error">{errorMessage}</p>
        )}
        {!isLoading && !errorMessage && hasAppliedSearch && resultCount === 0 && (
          <p>{t("weather_groups_results_empty")}</p>
        )}
        {!isLoading && !errorMessage && resultCount > 0 && (
          <>
            <p className="anglers-radius-results-count">
              {t("weather_groups_results_count", { count: resultCount })}
            </p>
            <div className="anglers-radius-results-list">
              {results.map((group) => {
                const metaBits = [
                  getVisibilityLabel(group.visibility),
                  getGroupTypeLabel(group.groupType),
                  group.locationName,
                ].filter(Boolean);

                return (
                  <button
                    key={group.id}
                    type="button"
                    className="groups-radius-result-item groups-radius-result-button"
                    onClick={() => {
                      if (onOpenGroup) {
                        onOpenGroup(group);
                      }
                    }}
                  >
                    <div className="groups-radius-result-header">
                      <strong>{group.name}</strong>
                      <span className="groups-radius-result-distance">
                        {formatDistanceLabel(group.distanceKm)}
                      </span>
                    </div>
                    {group.description ? (
                      <p className="groups-radius-result-description">
                        {group.description}
                      </p>
                    ) : null}
                    {metaBits.length > 0 ? (
                      <p className="groups-radius-result-meta">
                        {metaBits.join(" | ")}
                      </p>
                    ) : null}
                    <div className="groups-radius-result-stats">
                      <span>
                        {t("weather_groups_results_members", {
                          count: group.memberCount,
                        })}
                      </span>
                      <span>
                        {t("weather_groups_results_admins", {
                          count: group.adminCount,
                        })}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}
        {hasAppliedSearch && !isLoading && !errorMessage ? (
          <p className="anglers-radius-results-meta">
            {t("weather_groups_results_radius", {
              radius: appliedRadiusKm,
              place: placeName,
            })}
          </p>
        ) : null}
      </div>
    </div>
  );
}
